import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, on } from '@ngrx/store';
import { Order } from '../../models/order.model';
import { OrderActions } from './orders.actions';
import { AuthActions } from '../auth/auth.actions';

export interface OrdersState extends EntityState<Order> {
  loading: boolean;
  selectedOrderId: number | null;
  error: string | null;
}

export const adapter = createEntityAdapter<Order>();

const initialState: OrdersState = adapter.getInitialState({
  loading: false,
  selectedOrderId: null,
  error: null,
});

export const ordersFeature = createFeature({
  name: 'orders',
  reducer: createReducer(
    initialState,
    on(OrderActions.loadOrders, (state) => ({ ...state, loading: true, error: null })),
    on(OrderActions.loadOrdersSuccess, (state, { orders }) =>
      adapter.setAll(orders, { ...state, loading: false })),
    on(OrderActions.loadOrdersFailure, (state, { error }) => ({ ...state, loading: false, error })),

    on(OrderActions.loadOrder, (state, { id }) => ({ ...state, selectedOrderId: id, loading: true })),
    on(OrderActions.loadOrderSuccess, (state, { order }) =>
      adapter.upsertOne(order, { ...state, loading: false })),

    on(OrderActions.createOrderSuccess, (state, { order }) => adapter.addOne(order, state)),
    on(OrderActions.updateOrderSuccess, (state, { order }) => adapter.upsertOne(order, state)),
    on(OrderActions.reopenOrderSuccess, (state, { order }) => adapter.upsertOne(order, state)),

    on(OrderActions.addComplaintSuccess, (state, { orderId, complaint }) => {
      const order = state.entities[orderId];
      if (!order) return state;
      return adapter.updateOne({ id: orderId, changes: { complaints: [...(order.complaints || []), complaint] } }, state);
    }),
    on(OrderActions.resolveComplaintSuccess, (state, { orderId, complaint }) => {
      const order = state.entities[orderId];
      if (!order) return state;
      return adapter.updateOne({
        id: orderId,
        changes: { complaints: (order.complaints || []).map(c => c.id === complaint.id ? complaint : c) }
      }, state);
    }),
    on(OrderActions.markComplaintWontDoSuccess, (state, { orderId, complaint }) => {
      const order = state.entities[orderId];
      if (!order) return state;
      return adapter.updateOne({
        id: orderId,
        changes: { complaints: (order.complaints || []).map(c => c.id === complaint.id ? complaint : c) }
      }, state);
    }),
    on(OrderActions.closeOrderSuccess, (state, { order }) => adapter.upsertOne(order, state)),

    on(OrderActions.deleteOrderSuccess, (state, { id }) =>
      adapter.removeOne(id, { ...state, loading: false })),

    on(OrderActions.orderFailure, (state, { error }) => ({ ...state, loading: false, error })),

    // ZK Phase 9.6 — decrypted PII must not outlive the session: wipe this slice on logout.
    on(AuthActions.logoutSuccess, () => initialState),
  )
});

export const {
  name: ordersFeatureKey,
  reducer,
  selectOrdersState,
  selectLoading: selectOrdersLoading,
  selectSelectedOrderId,
  selectError: selectOrdersError,
} = ordersFeature;

export const { selectAll, selectEntities } = adapter.getSelectors();
